import { keyframes } from '@mui/material';
import { BG_COLOR, CLOUD_MIST, DISPUTED, MIXED, SUPPORTED } from 'utils/theme';

const FREE_VERSION_CHARACTER_COUNT = 10000;

export const EXPANDED_DESKTOP_NAVBAR_WIDTH = 250;
export const EXPANDED_TABLET_NAVBAR_WIDTH = 310;
export const NAVBAR_WIDTH = 145;
export const DESKTOP_WIDTH_BREAKPOINT = 1193;
export const wordsCount = (str) => {
  return str.trim().split(/\s+/).length;
};

export const charCount = (str: string) => str.trim().length;

export const isTooLongText = (searchValue: string) =>
  charCount(searchValue) > FREE_VERSION_CHARACTER_COUNT;

export enum SourceDates {
  MOST_RECENT = 'MOST_RECENT',
  LEAST_RECENT = 'LEAST_RECENT',
}

export enum ErrorCodes {
  TIMEOUT = 'TIMEOUT',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum PublishedSinceDates {
  ANY = '0',
  THREE_MONTHS = '3',
  SIX_MONTHS = '6',
  ONE_YEAR = '12',
  TWO_YEARS = '24',
}

export enum RelevanceType {
  MOST_RELEVANT = 'MOST_RELEVANT',
  LEAST_RELEVANT = 'LEAST_RELEVANT',
}

export type SearchEngine = {
  name: string;
  label?: string;
  tooltip?: string;
};

export const searchEngines: SearchEngine[] = [
  { name: 'Google' },
  { name: 'Google Claim' },
  { name: 'DuckDuckGo' },
  { name: 'Wikipedia' },
  { name: 'Semantic Scholar' },
  {
    name: 'ElasticSearch',
    label: 'FactiSearch',
    tooltip:
      'FactiSearch is the worlds largest database of fact-checks, tracking fact-checks in more than 90 languages.',
  },
  {
    name: 'SSB',
    label: 'Statistisk Sentralbyrå',
    tooltip: "Norway's statistics bureau",
  },
  {
    name: 'SNL',
    label: 'Store Norske Leksikon',
    tooltip: 'Norwegian Encyclopedia',
  },
];

export enum SourceTypes {
  SUPPORTING = 'supporting',
  MIXED = 'mixed',
  DISPUTING = 'disputing',
  INCONCLUSIVE = 'inconclusive',
  ALL = 'all',
  NONE = 'none',
}

export enum ClaimTypes {
  SUPPORTED = 'supported',
  DISPUTED = 'disputed',
  MIXED = 'claim_mixed',
  UNVERIFIED = 'unverified',
}

enum SourceLabelDescription {
  SUPPORTS = 'SUPPORTS',
  DISPUTES = 'DISPUTES',
}

export enum SourceLabelMap {
  SUPPORTS = 'SUPPORTS',
  REFUTES = 'REFUTES',
  MIXED = 'MIXED',
  NOT_ENOUGH_INFO = 'NOT_ENOUGH_INFO',
}

// Default label map used when Redux state labelMap is not available (e.g., in URL check results)
export const DEFAULT_LABEL_MAP: Record<string, number> = {
  REFUTES: 0,
  SUPPORTS: 1,
  MIXED: 2,
  NOT_ENOUGH_INFO: 3,
};

export const getSourceTypeFromPredictedLabel = (
  predictedLabel: number | undefined,
  labelMap: Record<string, number>
): SourceTypes => {
  if (predictedLabel === undefined || !labelMap) return SourceTypes.NONE;

  const labelName = Object.keys(labelMap).find((key) => labelMap[key] === predictedLabel);

  switch (labelName) {
    case SourceLabelMap.SUPPORTS:
      return SourceTypes.SUPPORTING;
    case SourceLabelMap.REFUTES:
      return SourceTypes.DISPUTING;
    case SourceLabelMap.MIXED:
      return SourceTypes.MIXED;
    case SourceLabelMap.NOT_ENOUGH_INFO:
      return SourceTypes.INCONCLUSIVE;
    default:
      return SourceTypes.NONE;
  }
};

const findLabelNameByValue = (value: number, labelMap: Record<string, number>): string | null => {
  const labelName = Object.keys(labelMap).find((key) => labelMap[key] === value);
  return labelName || null;
};

export const getFinalLabelDescriptionFromPrediction = (
  finalPrediction: number | null | undefined,
  labelMap: Record<string, number>
): string | null => {
  if (finalPrediction === null || finalPrediction === undefined || !labelMap) return null;
  return findLabelNameByValue(finalPrediction, labelMap);
};

const LABEL_TO_DISPLAY_MAP: Record<SourceLabelMap, string> = {
  [SourceLabelMap.SUPPORTS]: 'supported',
  [SourceLabelMap.REFUTES]: 'disputed',
  [SourceLabelMap.MIXED]: 'mixed',
  [SourceLabelMap.NOT_ENOUGH_INFO]: 'unverified',
};

export const getLabelFromDescription = (finalLabelDescription?: string): string | null => {
  if (!finalLabelDescription) return null;

  const isValidLabel = Object.values(SourceLabelMap).includes(
    finalLabelDescription as SourceLabelMap
  );

  return isValidLabel
    ? LABEL_TO_DISPLAY_MAP[finalLabelDescription as SourceLabelMap]
    : 'unverified';
};

export const sourceTypeValues = [SourceTypes.DISPUTING, SourceTypes.MIXED, SourceTypes.SUPPORTING];

export const getErrorText = (searchValue: string) => {
  if (charCount(searchValue) > FREE_VERSION_CHARACTER_COUNT) {
    return 'Input exceeds 10000 characters, please analyse a shorter text.';
  }
};

export function areArraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();
  return sortedArr1.every((value, index) => value === sortedArr2[index]);
}

export const shuffleArray = (array) => {
  return array
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);
};

const MAX_SUGGESTED_CLAIMS = 4;
const MAX_CLAIM_LENGTH = 50;

export const filterSuggestedClaims = (claims: string[]): string[] => {
  const filtered: string[] = [];
  let hasLongClaim = false;

  for (const claim of claims) {
    if (filtered.length >= MAX_SUGGESTED_CLAIMS) {
      break;
    }

    const isLongClaim = claim.length > MAX_CLAIM_LENGTH;
    if (isLongClaim && hasLongClaim) {
      continue;
    }

    filtered.push(claim);
    if (isLongClaim) {
      hasLongClaim = true;
    }
  }

  return filtered;
};

export const sortSourcesByDate = (sources: Source[], selectedSourceDate: SourceDates) => {
  const sourcesCopy = [...sources];
  const nullOldDate = new Date(70, 0, 1);
  const nullNewDate = new Date();
  if (selectedSourceDate === null) return sources;
  else if (selectedSourceDate === SourceDates.MOST_RECENT) {
    return sourcesCopy?.sort(
      (a, b) =>
        new Date(b.publishDate ?? nullOldDate).getTime() -
        new Date(a.publishDate ?? nullOldDate).getTime()
    );
  } else if (selectedSourceDate === SourceDates.LEAST_RECENT) {
    return sourcesCopy?.sort(
      (a, b) =>
        new Date(a.publishDate ?? nullNewDate).getTime() -
        new Date(b.publishDate ?? nullNewDate).getTime()
    );
  }
};

export const extractSummaryTextFromClaim = (claim: Claim) => {
  const { claim: claimText, evidence, summary, finalPrediction } = claim;

  const supportingSources =
    evidence?.filter((e) => e.labelDescription === SourceLabelDescription.SUPPORTS).length || 0;
  const disputingSources =
    evidence?.filter((e) => e.labelDescription === SourceLabelDescription.DISPUTES).length || 0;
  const mixedSources = evidence && evidence.length - (supportingSources + disputingSources);

  let summaryText = `Claim: ${claimText}\nSupporting sources: ${supportingSources}, Mixed sources: ${mixedSources}, Disputing sources: ${disputingSources}`;

  if (summary?.length) {
    summaryText += `\n\nThe sources ${
      finalPrediction === 1 ? 'support' : 'dispute'
    } this claim:\n\n${summary.map((summaryItem) => `- ${summaryItem}`).join('\n')}`;
  } else {
    summaryText += `\n\nNo summary available.`;
  }

  if (evidence && evidence.length > 0) {
    summaryText += `\n\nWe have found evidence from:\n\n${evidence
      .map((evidenceItem) => `[${evidenceItem.title}](${evidenceItem.url})`)
      .join('\n\n')}`;
  }

  return summaryText;
};

export const handleCopyToClipboard = async (textToCopy) => {
  try {
    await navigator.clipboard.writeText(textToCopy);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console -- clipboard API failures are surfaced in dev only
    console.error('Failed to copy text: ', err);
    return false;
  }
};

export function containsInteger(str) {
  const integerRegex = /\b\d+\b/;
  return integerRegex.test(str);
}

export const groupSources = (
  evidence: Source[],
  labelMap: Record<string, number>,
  getSourceLabel: (label: number, labelMap: Record<string, number>) => string
) => {
  const groups: Record<SourceTypes, Source[]> = {
    [SourceTypes.SUPPORTING]: [],
    [SourceTypes.MIXED]: [],
    [SourceTypes.DISPUTING]: [],
    [SourceTypes.INCONCLUSIVE]: [],
    [SourceTypes.ALL]: [],
    [SourceTypes.NONE]: [],
  };

  for (const item of evidence) {
    const sourceType = getSourceLabel(item.predictedLabel, labelMap) as SourceTypes;
    groups[SourceTypes.ALL].push(item);
    groups[sourceType]?.push(item);
  }

  return {
    supportingSources: groups[SourceTypes.SUPPORTING],
    mixedSources: groups[SourceTypes.MIXED],
    disputingSources: groups[SourceTypes.DISPUTING],
    unknownSources: groups[SourceTypes.INCONCLUSIVE],
    groupedSourceTypes: groups,
  };
};

export const HIGHLIGHT_ANIMATION_DURATION_MS = 4000;
export const CLAIM_HIGHLIGHT_ANIMATION_DURATION_MS = 2000;

export const createHighlightAnimation = (color: string) => {
  return keyframes`
    0% {
      background-color: ${color};
      box-shadow: 0 0 0 0 ${color};
    }
    50% {
      background-color: ${color};
      box-shadow: 0 0 0 2px ${color};
    }
    100% {
      background-color: transparent;
      box-shadow: 0 0 0 0 ${color};
    }
  `;
};

export const getSourceLabelColor = (label: string): string => {
  switch (label) {
    case SourceTypes.SUPPORTING:
    case 'supported':
      return SUPPORTED;
    case SourceTypes.DISPUTING:
    case 'disputed':
      return DISPUTED;
    case SourceTypes.MIXED:
    case 'mixed':
      return MIXED;
    case SourceTypes.INCONCLUSIVE:
    case 'unverified':
      return CLOUD_MIST;
    default:
      return BG_COLOR;
  }
};
